/**
 * Cache wrapper for "shift dropoffs since cutoff" — the scheduled stops joined to the
 * shifts the Scheduling page loads. TtlCache keyed by the cutoff's LOCAL calendar date
 * (calendar-day rollover invalidates), filtering on the stops' `schedDate` lookup,
 * mirroring `CachedShiftsSinceCutoff`.
 */

import dayjs from "dayjs";
import { ShiftDropoff } from "@domain/CRM/dropoff";
import { TtlCache } from "../../../cache/ttlCache";
import { API } from "../../../api";

const SINCE_CUTOFF_TTL_MS = 15 * 60 * 1000;

/** The one query behind both the TTL fetcher and `refresh()`; `fresh` also bypasses the
 *  server-side Airtable cache (`?fresh=1`). */
const fetchStopsSince = (cutoffDay: string, fresh?: boolean): Promise<ShiftDropoff[]> =>
	API.Data.ShiftDropoffs.get.byFilter({ fields: { schedDate: { operator: ">=", value: cutoffDay } } }, fresh);

class CachedShiftDropoffsSinceCutoff {
	private cache = new TtlCache<string, ShiftDropoff[]>(SINCE_CUTOFF_TTL_MS, (cutoffDay) => fetchStopsSince(cutoffDay));

	/** `Sched Date` is a date-only lookup (GMT midnight in the AT formula) — a toISOString()
	 *  cutoff (local midnight → e.g. T04:00:00Z) would land after it and drop TODAY'S stops. */
	private static keyFor(cutoff: Date): string {
		return dayjs(cutoff).format("YYYY-MM-DD");
	}

	/** Synchronous TTL cache hit for the given cutoff, or null if missing/expired. */
	public peek(cutoff: Date): ShiftDropoff[] | null {
		return this.cache.peek(CachedShiftDropoffsSinceCutoff.keyFor(cutoff));
	}

	public get(cutoff: Date): Promise<ShiftDropoff[]> {
		return this.cache.get(CachedShiftDropoffsSinceCutoff.keyFor(cutoff));
	}

	/** Bypass both caches and overwrite the entry with the live result — see
	 *  `CachedShiftsSinceCutoff.refresh`. A failed fetch leaves the previous entry in place. */
	public async refresh(cutoff: Date): Promise<ShiftDropoff[]> {
		const key = CachedShiftDropoffsSinceCutoff.keyFor(cutoff);
		const stops = await fetchStopsSince(key, true);
		this.cache.set(key, stops);
		return stops;
	}

	public clear(): void {
		this.cache.clear();
	}
}

export class CachedShiftDropoffsAPI {
	public sinceCutoff = new CachedShiftDropoffsSinceCutoff();
}
